import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, DollarSign, Percent, PiggyBank, Info, TrendingUp, AlertCircle, Crown, Lock } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { getToken } from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL;

// 2024/2025 Tax Brackets for Self-Employment
const FEDERAL_TAX_BRACKETS = [
  { min: 0, max: 11600, rate: 0.10 },
  { min: 11600, max: 47150, rate: 0.12 },
  { min: 47150, max: 100525, rate: 0.22 },
  { min: 100525, max: 191950, rate: 0.24 },
  { min: 191950, max: 243725, rate: 0.32 },
  { min: 243725, max: 609350, rate: 0.35 },
  { min: 609350, max: Infinity, rate: 0.37 }
];

// Self-Employment Tax Rate (Social Security + Medicare)
const SE_TAX_RATE = 0.153; // 15.3% (12.4% Social Security + 2.9% Medicare)
const SE_DEDUCTION_RATE = 0.5; // Can deduct 50% of SE tax

// 1099 Tax Estimator Component (Premium Feature)
function TaxEstimator1099Section() {
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  const [is1099, setIs1099] = useState(false);
  const [incomeData, setIncomeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // Check if user is 1099
        const statusRes = await fetch(`${API}/api/1099/status`, {
          credentials: 'include',
          headers,
        });
        
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setIs1099(statusData.is_1099 || false);
          
          // If 1099 and premium, load income data
          if (statusData.is_1099 && isPremium()) {
            const incomeRes = await fetch(`${API}/api/1099/income`, {
              credentials: 'include',
              headers,
            });
            
            if (incomeRes.ok) {
              const data = await incomeRes.json();
              setIncomeData(data);
            }
          }
        }
      } catch (error) {
        console.error('Error loading 1099 data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [isPremium]);

  // Don't show section if not premium
  if (!isPremium()) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center">
            <Lock className="w-7 h-7 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-purple-900 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              1099 Income Tracker
            </h3>
            <p className="text-sm text-purple-700 mt-1">
              Track your monthly 1099 income automatically when you wrap up each month. See YTD totals and tax estimates.
            </p>
          </div>
          <button
            onClick={() => navigate('/app/pricing')}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 transition-all text-sm"
            data-testid="upgrade-1099-btn"
          >
            Upgrade
          </button>
        </div>
      </div>
    );
  }

  // Don't show if user hasn't enabled 1099 mode
  if (!is1099) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center text-3xl">
            📋
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-amber-900">1099 Income Tracking</h3>
            <p className="text-sm text-amber-700 mt-1">
              Enable 1099 mode on the Income page to start tracking your self-employment income. Your income will be automatically archived when you wrap up each month.
            </p>
          </div>
          <button
            onClick={() => navigate('/app/income')}
            className="px-4 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-all text-sm"
            data-testid="enable-1099-btn"
          >
            Enable 1099
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  const entries = incomeData?.entries || [];
  const ytdTotal = incomeData?.ytd_total || 0;
  const currentYear = incomeData?.year || new Date().getFullYear();

  // Calculate estimated taxes on tracked income
  const estimatedSETax = ytdTotal * 0.9235 * 0.153;
  const estimatedFederalTax = ytdTotal * 0.15;
  const totalEstimatedTax = estimatedSETax + estimatedFederalTax;
  const quarterlyPayment = totalEstimatedTax / 4;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6 mb-6" data-testid="tax-estimator-1099-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-emerald-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-600" />
          Your 1099 Income Tracker ({currentYear})
        </h3>
        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
          <Crown className="w-3 h-3" /> Premium
        </span>
      </div>

      {/* YTD Summary Card */}
      <div className="bg-white rounded-xl p-4 mb-4 border border-emerald-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xs text-gray-500">YTD Income</p>
            <p className="text-2xl font-bold text-emerald-600">${ytdTotal.toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Est. SE Tax</p>
            <p className="text-2xl font-bold text-amber-600">${Math.round(estimatedSETax).toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Est. Federal</p>
            <p className="text-2xl font-bold text-blue-600">${Math.round(estimatedFederalTax).toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Est. Quarterly</p>
            <p className="text-2xl font-bold text-purple-600">${Math.round(quarterlyPayment).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      {entries.length > 0 ? (
        <div className="bg-white rounded-xl border border-emerald-100 overflow-hidden">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100">
            <h4 className="font-medium text-emerald-800 text-sm">Monthly Breakdown</h4>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {entries.map((entry, idx) => (
              <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">{entry.month_name}</p>
                  <p className="text-xs text-gray-500">
                    {entry.source === 'wrap_up' ? 'Auto-archived' : 'Manual entry'}
                  </p>
                </div>
                <span className="font-bold text-emerald-600">${entry.amount.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 text-center border border-emerald-100">
          <div className="text-4xl mb-2">📋</div>
          <p className="text-gray-600 font-medium">No income tracked yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Your income will appear here when you wrap up each month on the Income page.
          </p>
        </div>
      )}

      <p className="text-xs text-emerald-600 mt-3 text-center">
        💡 These are estimates. Use the calculator below for more accurate projections.
      </p>
    </div>
  );
}

function TaxCalculator1099() {
  const navigate = useNavigate();
  const [annualIncome, setAnnualIncome] = useState('');
  const [businessExpenses, setBusinessExpenses] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const [quarterlyPayments, setQuarterlyPayments] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  const calculateTaxes = () => {
    const income = parseFloat(annualIncome) || 0;
    const expenses = parseFloat(businessExpenses) || 0;
    const quarterly = parseFloat(quarterlyPayments) || 0;

    // Net self-employment income
    const netIncome = Math.max(0, income - expenses);

    // Self-Employment Tax (on 92.35% of net income)
    const seIncomeBase = netIncome * 0.9235;
    const seTax = seIncomeBase * SE_TAX_RATE;
    
    // Deductible portion of SE tax
    const seDeduction = seTax * SE_DEDUCTION_RATE;

    // Adjusted Gross Income for federal tax
    const agi = netIncome - seDeduction;

    // Standard deduction based on filing status (2024)
    const standardDeductions = {
      single: 14600,
      married_joint: 29200,
      married_separate: 14600,
      head_of_household: 21900
    };
    const standardDeduction = standardDeductions[filingStatus];

    // Taxable income
    const taxableIncome = Math.max(0, agi - standardDeduction);

    // Calculate federal income tax using brackets
    let federalTax = 0;
    let remainingIncome = taxableIncome;
    
    for (const bracket of FEDERAL_TAX_BRACKETS) {
      if (remainingIncome <= 0) break;
      const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
      federalTax += taxableInBracket * bracket.rate;
      remainingIncome -= taxableInBracket;
    }

    // Total estimated tax
    const totalTax = seTax + federalTax;

    // Quarterly payment recommendation
    const quarterlyRecommendation = totalTax / 4;

    // Already paid
    const alreadyPaid = quarterly * 4;
    const remainingTax = Math.max(0, totalTax - alreadyPaid);

    // Effective tax rate
    const effectiveRate = netIncome > 0 ? (totalTax / netIncome) * 100 : 0;

    // Monthly savings recommendation (with 10% buffer)
    const monthlyToSave = (totalTax * 1.1) / 12;

    setResults({
      grossIncome: income,
      businessExpenses: expenses,
      netIncome,
      seTax,
      seDeduction,
      agi,
      standardDeduction,
      taxableIncome,
      federalTax,
      totalTax,
      quarterlyRecommendation,
      alreadyPaid,
      remainingTax,
      effectiveRate,
      monthlyToSave
    });
    setShowResults(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (percent) => {
    return `${percent.toFixed(1)}%`;
  };

  // Quarterly due dates
  const quarterlyDates = [
    { quarter: 'Q1', period: 'Jan 1 - Mar 31', dueDate: 'April 15' },
    { quarter: 'Q2', period: 'Apr 1 - May 31', dueDate: 'June 15' },
    { quarter: 'Q3', period: 'Jun 1 - Aug 31', dueDate: 'September 15' },
    { quarter: 'Q4', period: 'Sep 1 - Dec 31', dueDate: 'January 15 (next year)' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 mr-4"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">1099 Tax Calculator</h1>
            <p className="text-sm text-gray-500">Self-Employment Tax Estimator</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* 1099 Income Tracker - Premium Feature */}
        <TaxEstimator1099Section />

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">For 1099 Contractors & Self-Employed</p>
              <p>This calculator estimates your federal income tax and self-employment tax (Social Security + Medicare). State taxes are not included.</p>
            </div>
          </div>
        </div>

        {/* Income Input Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Income & Expenses
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Annual Gross Income (1099)
              </label>
              <input
                type="number"
                value={annualIncome}
                onChange={(e) => setAnnualIncome(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="annual-income-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Expenses (Deductible)
              </label>
              <input
                type="number"
                value={businessExpenses}
                onChange={(e) => setBusinessExpenses(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="business-expenses-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include supplies, equipment, home office, mileage, etc.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filing Status
              </label>
              <select
                value={filingStatus}
                onChange={(e) => setFilingStatus(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="filing-status-select"
              >
                <option value="single">Single</option>
                <option value="married_joint">Married Filing Jointly</option>
                <option value="married_separate">Married Filing Separately</option>
                <option value="head_of_household">Head of Household</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quarterly Payments Already Made (per quarter)
              </label>
              <input
                type="number"
                value={quarterlyPayments}
                onChange={(e) => setQuarterlyPayments(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="quarterly-payments-input"
              />
            </div>
          </div>

          <button
            onClick={calculateTaxes}
            disabled={!annualIncome}
            className="w-full mt-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            data-testid="calculate-btn"
          >
            <Calculator className="w-5 h-5" />
            Calculate My Taxes
          </button>
        </div>

        {/* Results Section */}
        {showResults && results && (
          <>
            {/* Summary Card */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-6 text-white shadow-lg">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Estimated Tax Summary
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-emerald-100 text-sm">Total Estimated Tax</p>
                  <p className="text-3xl font-bold">{formatCurrency(results.totalTax)}</p>
                </div>
                <div>
                  <p className="text-emerald-100 text-sm">Effective Tax Rate</p>
                  <p className="text-3xl font-bold">{formatPercent(results.effectiveRate)}</p>
                </div>
              </div>

              <div className="bg-white/20 rounded-lg p-4">
                <p className="text-emerald-100 text-sm mb-1">💰 Recommended Monthly Savings</p>
                <p className="text-2xl font-bold">{formatCurrency(results.monthlyToSave)}</p>
                <p className="text-emerald-100 text-xs mt-1">Includes 10% buffer for safety</p>
              </div>
            </div>

            {/* Breakdown Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Tax Breakdown</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Gross Income</span>
                  <span className="font-medium">{formatCurrency(results.grossIncome)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Business Expenses</span>
                  <span className="font-medium text-red-600">-{formatCurrency(results.businessExpenses)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 bg-gray-50 -mx-6 px-6">
                  <span className="font-medium text-gray-900">Net Self-Employment Income</span>
                  <span className="font-bold">{formatCurrency(results.netIncome)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Self-Employment Tax (15.3%)</span>
                  <span className="font-medium">{formatCurrency(results.seTax)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">SE Tax Deduction (50%)</span>
                  <span className="font-medium text-green-600">-{formatCurrency(results.seDeduction)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Standard Deduction</span>
                  <span className="font-medium text-green-600">-{formatCurrency(results.standardDeduction)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100 bg-gray-50 -mx-6 px-6">
                  <span className="font-medium text-gray-900">Taxable Income</span>
                  <span className="font-bold">{formatCurrency(results.taxableIncome)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Federal Income Tax</span>
                  <span className="font-medium">{formatCurrency(results.federalTax)}</span>
                </div>
                <div className="flex justify-between py-3 bg-emerald-50 -mx-6 px-6 rounded-b-lg">
                  <span className="font-bold text-emerald-900">Total Estimated Tax</span>
                  <span className="font-bold text-emerald-900">{formatCurrency(results.totalTax)}</span>
                </div>
              </div>
            </div>

            {/* Quarterly Payments Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <PiggyBank className="w-5 h-5 text-purple-600" />
                Quarterly Payment Schedule
              </h3>
              
              <div className="bg-purple-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-purple-900 font-medium">Recommended per Quarter</span>
                  <span className="text-2xl font-bold text-purple-900">{formatCurrency(results.quarterlyRecommendation)}</span>
                </div>
              </div>

              {results.alreadyPaid > 0 && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-green-800">Already Paid (YTD)</span>
                    <span className="font-bold text-green-800">{formatCurrency(results.alreadyPaid)}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-green-800">Remaining to Pay</span>
                    <span className="font-bold text-green-800">{formatCurrency(results.remainingTax)}</span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {quarterlyDates.map((q) => (
                  <div key={q.quarter} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <span className="font-medium text-gray-900">{q.quarter}</span>
                      <span className="text-gray-500 text-sm ml-2">({q.period})</span>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-500 text-sm">Due: </span>
                      <span className="font-medium">{q.dueDate}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Disclaimer</p>
                  <p>This is an estimate only. Actual taxes may vary based on your specific situation, state taxes, additional deductions, and credits. Consult a tax professional for personalized advice.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TaxCalculator1099;
